<template>
  <div>
    <div
      v-if="shortcutOptions.length || taskShortcutOptions.length"
      class="d-flex justify-content-end align-items-center mb-3"
    >
      <button class="btn btn-outline-secondary mr-2" type="button" @click="toggleShortcutManager">
        <i class="fa" :class="showShortcutManager ? 'fa-times' : 'fa-cog'"></i>
        <span class="ml-1">{{
          showShortcutManager ? "Close manager" : "Manage shortcuts"
        }}</span>
      </button>
      <button 
        v-if="taskShortcutOptions.length"
        class="btn btn-outline-primary" 
        type="button" 
        @click="toggleTaskShortcutManager"
      >
        <i class="fa" :class="showTaskShortcutManager ? 'fa-times' : 'fa-bolt'"></i>
        <span class="ml-1">{{
          showTaskShortcutManager ? "Close manager" : "Manage task shortcuts"
        }}</span>
      </button>
    </div>
    <div v-if="showShortcutManager" class="card shortcut-manager mb-4">
      <div class="card-body">
        <p class="mb-3 text-muted">
          Choose which destinations should appear as shortcut buttons. Only the
          pages you have access to are listed.
        </p>
        <div class="shortcut-manager-list">
          <div
            class="shortcut-category"
            v-for="group in groupedShortcutOptions"
            :key="group.label"
          >
            <p class="shortcut-category-title mb-2">
              {{ formatCategoryLabel(group.label) }}
            </p>
            <div
              class="form-check py-1"
              v-for="option in group.options"
              :key="option.routeName"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :value="option.routeName"
                v-model="shortcutDraftSelection"
                :id="`shortcut-${option.routeName}`"
              />
              <label class="form-check-label" :for="`shortcut-${option.routeName}`">
                <span class="d-flex flex-column">
                  <strong>{{ option.title }}</strong>
                  <small class="text-muted">{{ option.fullPathLabel }}</small>
                </span>
              </label>
            </div>
          </div>
        </div>
        <div class="text-right mt-3">
          <button class="btn btn-link mr-2" type="button" @click="clearShortcutDraft">
            Clear all
          </button>
          <button class="btn btn-secondary mr-2" type="button" @click="cancelShortcutChanges">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="button"
            :disabled="!shortcutChangesPending"
            @click="saveShortcutSelection"
          >
            Save
          </button>
        </div>
      </div>
    </div>
    <!-- Task Shortcut Manager -->
    <div v-if="showTaskShortcutManager" class="card shortcut-manager task-shortcut-manager mb-4">
      <div class="card-body">
        <p class="mb-3 text-muted">
          Choose which tasks should appear as quick-run buttons on your dashboard. 
          Only the tasks you have permission to run are listed.
        </p>
        <div class="mb-3">
          <input
            class="form-control"
            v-model="taskShortcutSearchText"
            placeholder="Search tasks..."
          />
        </div>
        <div class="shortcut-manager-list">
          <div
            class="form-check py-1"
            v-for="task in filteredTaskShortcutOptions"
            :key="task.name"
          >
            <input
              class="form-check-input"
              type="checkbox"
              :value="task.name"
              v-model="taskShortcutDraftSelection"
              :id="`task-shortcut-${task.name}`"
            />
            <label class="form-check-label" :for="`task-shortcut-${task.name}`">
              <span class="d-flex flex-column">
                <strong>{{ task.description }}</strong>
                <small class="text-muted">{{ task.name }}</small>
              </span>
            </label>
          </div>
        </div>
        <div class="text-right mt-3">
          <button class="btn btn-link mr-2" type="button" @click="clearTaskShortcutDraft">
            Clear all
          </button>
          <button class="btn btn-secondary mr-2" type="button" @click="cancelTaskShortcutChanges">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="button"
            :disabled="!taskShortcutChangesPending"
            @click="saveTaskShortcutSelection"
          >
            Save
          </button>
        </div>
      </div>
    </div>
    <div class="row row-spacing">
      <div
        class="col-md-4"
        v-for="widget in dashboardLayout.widgets"
        :key="widget.title"
      >
        <div class="card border-0">
          <div class="row row-flush">
            <div
              :class="getWidgetColour(widget)"
              class="
                col-4
                text-center
                d-flex
                align-items-center
                justify-content-center
                rounded-left
              "
            >
              <em><i :class="getWidgetIcon(widget)"></i></em>
            </div>
            <div class="col-8">
              <div class="card-body text-center">
                <h4 class="mt-0">{{ widget.value }}</h4>
                <p class="mb-0 text-muted">{{ widget.title }}</p>
                <small class="">{{ widget.subtitle }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AgentClients
      v-if="clientsList.length > 0"
      :clientsList="clientsList"
      :loadingState="loadingState"
    />
    <div v-if="selectedShortcuts.length" class="card shortcut-buttons mt-4">
      <div class="card-body">
        <div class="row">
          <div
            class="col-xl-3 col-lg-4 col-md-6 mb-3"
            v-for="shortcut in selectedShortcuts"
            :key="shortcut.routeName"
          >
            <button
              class="btn btn-shortcut w-100 h-100 text-left"
              type="button"
              @click="goToShortcut(shortcut)"
            >
              <div class="d-flex align-items-center">
                <div class="shortcut-icon mr-3">
                  <i :class="shortcut.iconClass"></i>
                </div>
                <div class="shortcut-copy">
                  <span class="shortcut-title d-block">{{ shortcut.title }}</span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Task Shortcut Buttons -->
    <div v-if="selectedTaskShortcuts.length" class="card task-shortcut-buttons mt-4">
      <div class="card-header bg-white border-bottom">
        <h5 class="mb-0"><i class="fa fa-bolt mr-2"></i>Quick Tasks</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div
            class="col-xl-3 col-lg-4 col-md-6 mb-3"
            v-for="task in selectedTaskShortcuts"
            :key="task.name"
          >
            <button
              class="btn btn-task-shortcut w-100 h-100 text-left"
              type="button"
              @click="openRunTaskModal(task)"
            >
              <div class="d-flex align-items-center">
                <div class="task-shortcut-icon mr-3">
                  <i class="fa fa-bolt"></i>
                </div>
                <div class="task-shortcut-copy">
                  <span class="task-shortcut-title d-block">{{ task.description }}</span>
                  <small class="text-muted">{{ task.name }}</small>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Run Task Modal -->
    <modal
      :title="'Run ' + selectedTaskDefinition.name + ' task now'"
      ref="runTaskModal"
      size="modal-lg"
      save-text="Run Task"
      @save="runTaskNow()"
    >
      <h4>Required</h4>
      <table
        class="table table-bordered"
        v-if="selectedTaskDefinition.requiredInputParams.length > 0"
      >
        <tbody>
          <tr
            v-for="inputParam in selectedTaskDefinition.requiredInputParams"
            :key="inputParam.taskParameterDefinitionNumber"
          >
            <td>
              <strong>{{ inputParam.description }}</strong>
            </td>
            <td>
              <input
                type="checkbox"
                v-model="inputParam.value"
                v-if="inputParam.type == 'BOOLEAN'"
              />
              <input
                type="text"
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'STRING'"
                class="form-control"
              />
              <input
                type="number"
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'INT'"
                class="form-control"
              />
              <input
                type="number"
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'DOUBLE'"
                class="form-control"
              />
              <input
                type="file"
                @change="fileSelected($event, inputParam)"
                v-else-if="inputParam.type == 'CONTENT'"
                class="form-control"
              />
              <DateInput
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'DATE'"
              />
              <strong style="color: red" v-else
                >Param type {{ inputParam.type }} not supported</strong
              >
              <strong style="color: red" v-if="inputParam.hasError"
                >{{ inputParam.name }} is required</strong
              >
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>No Required Parameters</p>
      <hr />
      <h5>Optional</h5>
      <table
        class="table table-bordered"
        v-if="selectedTaskDefinition.optionalInputParams.length > 0"
      >
        <tbody>
          <tr
            v-for="inputParam in selectedTaskDefinition.optionalInputParams"
            :key="inputParam.taskParameterDefinitionNumber"
          >
            <td>
              <p>{{ inputParam.description }}</p>
            </td>
            <td>
              <input
                type="checkbox"
                v-model="inputParam.value"
                v-if="inputParam.type == 'BOOLEAN'"
              />
              <input
                type="text"
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'STRING'"
                class="form-control"
              />
              <input
                type="number"
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'INT'"
                class="form-control"
              />
              <input
                type="number"
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'DOUBLE'"
                class="form-control"
              />
              <input
                type="file"
                @change="fileSelected($event, inputParam)"
                v-else-if="inputParam.type == 'CONTENT'"
                class="form-control"
              />
              <DateInput
                v-model="inputParam.value"
                v-else-if="inputParam.type == 'DATE'"
              />
              <strong style="color: red" v-else
                >Param type {{ inputParam.type }} not supported</strong
              >
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>No Optional Parameters</p>
    </modal>
    <!-- Bottom spacer for scroll breathing room -->
    <div class="dashboard-bottom-spacer"></div>
  </div>
</template>

<script>
import AgentClients from "@/components/Dashboard/AgentClients";
import Modal from "@/components/GeneralComponents/Modal";
import DateInput from "@/components/GeneralComponents/InputControls/DateInput";
const homeNavbarStructure = require("@/structures/homeNavbarStructure.json");

export default {
  components: {
    AgentClients,
    Modal,
    DateInput,
  },
  data() {
    return {
      dashboardLoading: true,
      loadingState: true,
      clientsList: [],
      dashboardLayout: {},
      notifications: [],
      allShortcutOptions: [],
      shortcutOptions: [],
      shortcutDraftSelection: [],
      selectedShortcutRouteNames: [],
      showShortcutManager: false,
      shortcutStorageKey: null,
      shortcutChangesPending: false,
      // Task shortcuts
      taskShortcutOptions: [],
      taskShortcutDraftSelection: [],
      selectedTaskShortcutNames: [],
      showTaskShortcutManager: false,
      taskShortcutStorageKey: null,
      taskShortcutChangesPending: false,
      taskShortcutSearchText: "",
      selectedTaskDefinition: {
        requiredInputParams: [],
        optionalInputParams: [],
        notifyEmail: {},
      },
    };
  },
  mounted() {
    if (this.$route.path !== "/main/dashboard") {
      this.$router.push("/main/dashboard");
    }
    const usageMonthDate = new Date();
    usageMonthDate.setDate(1);
    usageMonthDate.setMonth(usageMonthDate.getMonth());

    this.usageYear = usageMonthDate.getFullYear();
    this.usageMonth = usageMonthDate.getMonth();

    this.$http.get(this.$config.aimsAPI + "dashboards/").then(
      (response) => {
        this.dashboardLayout = response.data;
      },
      (error) => {
        console.error(error);
        this.showError("Error Fetching Dashboard", error);
      }
    );
    this.$http.get(this.$config.aimsAPI + "alerts").then(
      (response) => {
        this.notifications = response.data;
      },
      (error) => {
        this.showError("Error fetching alerts", error);
        console.error(error);
      }
    );
    this.getclientList();
    this.initializeShortcuts();
    this.initializeTaskShortcuts();
  },
  computed: {
    selectedShortcuts() {
      return this.selectedShortcutRouteNames
        .map((routeName) =>
          this.shortcutOptions.find((option) => option.routeName === routeName)
        )
        .filter((option) => option);
    },
    selectedTaskShortcuts() {
      return this.selectedTaskShortcutNames
        .map((taskName) =>
          this.taskShortcutOptions.find((option) => option.name === taskName)
        )
        .filter((option) => option);
    },
    filteredTaskShortcutOptions() {
      if (this.taskShortcutSearchText && this.taskShortcutSearchText.length > 0) {
        return this.taskShortcutOptions.filter(
          (t) =>
            t.name.toLowerCase().indexOf(this.taskShortcutSearchText.toLowerCase()) != -1 ||
            t.description.toLowerCase().indexOf(this.taskShortcutSearchText.toLowerCase()) != -1
        );
      } else {
        return this.taskShortcutOptions;
      }
    },
    groupedShortcutOptions() {
      const groupMap = new Map();
      this.shortcutOptions.forEach((option) => {
        const label = option.categoryLabel || "Top Level";
        if (!groupMap.has(label)) {
          groupMap.set(label, {
            label,
            orderTrail: option.categoryOrderTrail || [9999],
            options: [],
          });
        }
        groupMap.get(label).options.push(option);
      });
      return Array.from(groupMap.values())
        .sort((groupA, groupB) =>
          this.compareOrderTrails(groupA.orderTrail, groupB.orderTrail)
        )
        .map((group) => ({
          label: group.label,
          options: group.options.slice().sort((a, b) => a.title.localeCompare(b.title)),
        }));
    },
  },
  methods: {
    getclientList: function() {
      const hostUrl = this.$config.aimsAPI;

       this.$http.get(`${hostUrl}staff/clients/assigned`).then(
        (response) => {
          this.clientsList = response.data;
          this.loadingState = false;
        }, 
        (error) => {
          console.error(error);
          this.showError("Error Fetching Logged In Staff", error);
        }
     );
    },
    async initializeShortcuts() {
      this.allShortcutOptions = this.extractShortcutOptions(
        homeNavbarStructure.menu
      );
      this.refreshShortcutPermissions();
      await this.resolveShortcutStorageKey();
      this.selectedShortcutRouteNames = this.loadStoredShortcuts();
      this.shortcutDraftSelection = [...this.selectedShortcutRouteNames];
    },
    extractShortcutOptions(menuItems, ancestors = [], orderTrail = []) {
      let options = [];
      menuItems.forEach((item, index) => {
        const trail = [...ancestors, item.title];
        const nextOrderTrail = [...orderTrail, index];
        if (item.children && item.children.length) {
          options = options.concat(
            this.extractShortcutOptions(item.children, trail, nextOrderTrail)
          );
        } else if (item.routeName) {
          const ancestorTitles = trail.slice(0, -1);
          const categoryLabel =
            ancestorTitles.length > 0 ? ancestorTitles.join(" / ") : "Top Level";
          const categoryOrderTrail =
            ancestorTitles.length > 0
              ? nextOrderTrail.slice(0, -1)
              : [nextOrderTrail[0] ?? 9999];
          const entry = {
            title: item.title,
            iconClass: item.iconClass || "fa fa-circle",
            routeName: item.routeName,
            routeParams: item.routeParams || {},
            routeQuery: item.routeQuery || {},
            groupPermissions: item.groupPermissions && item.groupPermissions.length
              ? item.groupPermissions
              : ["*"],
            fullPathLabel: trail.join(" / "),
            categoryLabel,
            categoryOrderTrail,
          };
          options.push(entry);
        }
      });
      return options;
    },
    refreshShortcutPermissions() {
      if (!this.allShortcutOptions.length) {
        return;
      }
      const availableRoutes = this.allShortcutOptions.filter((option) =>
        this.userHasShortcutAccess(option)
      );
      const previouslySelected = [...this.selectedShortcutRouteNames];
      this.shortcutOptions = availableRoutes;
      this.selectedShortcutRouteNames = this.normalizeSelection(previouslySelected);
      this.shortcutDraftSelection = [...this.selectedShortcutRouteNames];
    },
    userHasShortcutAccess(option) {
      if (!option.groupPermissions || option.groupPermissions.length === 0) {
        return true;
      }
      return this.userContainsPermission(option.groupPermissions);
    },
    async resolveShortcutStorageKey() {
      if (this.shortcutStorageKey) {
        return;
      }
      let identifier = "default";
      try {
        const response = await this.$http.get(
          this.$config.aimsAPI + "users/user/loggedInUser"
        );
        identifier =
          response.data.userName ||
          response.data.emailAddress ||
          response.data.id ||
          identifier;
      } catch (error) {
        console.warn("Unable to load user details for shortcuts", error);
      }
      this.shortcutStorageKey = `staff-dashboard-shortcuts-${identifier}`;
    },
    loadStoredShortcuts() {
      if (!this.shortcutStorageKey) {
        return [];
      }
      const stored = this.$localStorage.get(this.shortcutStorageKey);
      if (!stored) {
        return [];
      }
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return this.normalizeSelection(parsed, this.allShortcutOptions);
        }
      } catch (error) {
        console.warn("Unable to parse stored shortcuts", error);
      }
      return [];
    },
    normalizeSelection(selection, sourceList = this.shortcutOptions) {
      const selectionSet = new Set(selection);
      return sourceList
        .filter((option) => selectionSet.has(option.routeName))
        .map((option) => option.routeName);
    },
    persistShortcuts() {
      if (!this.shortcutStorageKey) {
        return;
      }
      this.$localStorage.set(
        this.shortcutStorageKey,
        JSON.stringify(this.selectedShortcutRouteNames)
      );
    },
    toggleShortcutManager() {
      this.showShortcutManager = !this.showShortcutManager;
      if (this.showShortcutManager) {
        this.shortcutDraftSelection = [...this.selectedShortcutRouteNames];
        this.shortcutChangesPending = false;
      }
    },
    clearShortcutDraft() {
      this.shortcutDraftSelection = [];
    },
    cancelShortcutChanges() {
      this.shortcutDraftSelection = [...this.selectedShortcutRouteNames];
      this.shortcutChangesPending = false;
      this.showShortcutManager = false;
    },
    saveShortcutSelection() {
      this.selectedShortcutRouteNames = this.normalizeSelection(
        this.shortcutDraftSelection
      );
      this.persistShortcuts();
      this.showShortcutManager = false;
      this.shortcutChangesPending = false;
    },
    goToShortcut(shortcut) {
      this.$root.$emit("SYNC_NAVBAR_FOR_ROUTE", shortcut.routeName);
      this.$router.push({
        name: shortcut.routeName,
        params: shortcut.routeParams,
        query: shortcut.routeQuery,
      });
    },
    compareOrderTrails(first = [], second = []) {
      const maxLength = Math.max(first.length, second.length);
      for (let i = 0; i < maxLength; i++) {
        const firstValue =
          typeof first[i] === "number" ? first[i] : 9999;
        const secondValue =
          typeof second[i] === "number" ? second[i] : 9999;
        if (firstValue !== secondValue) {
          return firstValue - secondValue;
        }
      }
      return 0;
    },
    formatCategoryLabel(label = "") {
      return label.replace(/_/g, " ");
    },
    getWidgetIcon(widget) {
      if (widget.icon === "users") {
        return "fa fa-users fa-2x";
      }

      if (widget.icon === "user-plus") {
        return "fa fa-user-plus fa-2x";
      }

      if (widget.icon === "money") {
        return "fa fa-money fa-2x";
      }
    },
    getWidgetColour(widget) {
      if (widget.colour === "green") {
        return "bg-green";
      }
      if (widget.colour === "blue") {
        return "bg-info";
      }
      if (widget.colour === "orange") {
        return "bg-warning";
      }
    },
    goTo(titleLink, { goTo }) {
      if (goTo == "document") {
        this.$router.push({
          name: "search",
          query: {
            documentNumber: titleLink,
          },
        });
      }

      if (goTo == "supportingrateofexchange") {
        this.$router.push({
          name: goTo,
          query: {
            product: titleLink,
          },
        });
      }
    },
    graphOptions(graph) {
      const options = {
        chart: {
          type: "line",
          xaxis: {
            type: "datetime",
            labels: {
              datetimeUTC: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          markers: {
            size: 0,
          },
          title: {
            text: graph.title,
          },
          subtitle: {
            text: graph.subtitle,
            align: "left",
          },
          fill: {},
          yaxis: {
            title: {
              text: "What",
            },
          },
        },
      };
      return options;
    },
    translateGraphSeries(graph) {
      const series = [];
      for (let key in graph.graphSeries) {
        const seriesObj = {
          name: key,
        };
        seriesObj.data = graph.graphSeries[key];
        series.push(seriesObj);
      }
      return series;
    },
    async initializeTaskShortcuts() {
      try {
        const response = await this.$http.get(
          this.$config.aimsAPI + "tasks/definitions"
        );
        this.taskShortcutOptions = response.data;
        await this.resolveTaskShortcutStorageKey();
        this.selectedTaskShortcutNames = this.loadStoredTaskShortcuts();
        this.taskShortcutDraftSelection = [...this.selectedTaskShortcutNames];
      } catch (error) {
        console.error("Error fetching task definitions", error);
        this.taskShortcutOptions = [];
      }
    },
    async resolveTaskShortcutStorageKey() {
      if (this.taskShortcutStorageKey) {
        return;
      }
      let identifier = "default";
      try {
        const response = await this.$http.get(
          this.$config.aimsAPI + "users/user/loggedInUser"
        );
        identifier =
          response.data.userName ||
          response.data.emailAddress ||
          response.data.id ||
          identifier;
      } catch (error) {
        console.warn("Unable to load user details for task shortcuts", error);
      }
      this.taskShortcutStorageKey = `staff-dashboard-task-shortcuts-${identifier}`;
    },
    loadStoredTaskShortcuts() {
      if (!this.taskShortcutStorageKey) {
        return [];
      }
      const stored = this.$localStorage.get(this.taskShortcutStorageKey);
      if (!stored) {
        return [];
      }
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return this.normalizeTaskSelection(parsed);
        }
      } catch (error) {
        console.warn("Unable to parse stored task shortcuts", error);
      }
      return [];
    },
    normalizeTaskSelection(selection) {
      const selectionSet = new Set(selection);
      return this.taskShortcutOptions
        .filter((option) => selectionSet.has(option.name))
        .map((option) => option.name);
    },
    persistTaskShortcuts() {
      if (!this.taskShortcutStorageKey) {
        return;
      }
      this.$localStorage.set(
        this.taskShortcutStorageKey,
        JSON.stringify(this.selectedTaskShortcutNames)
      );
    },
    toggleTaskShortcutManager() {
      this.showTaskShortcutManager = !this.showTaskShortcutManager;
      if (this.showTaskShortcutManager) {
        this.taskShortcutDraftSelection = [...this.selectedTaskShortcutNames];
        this.taskShortcutChangesPending = false;
        this.taskShortcutSearchText = "";
      }
    },
    clearTaskShortcutDraft() {
      this.taskShortcutDraftSelection = [];
    },
    cancelTaskShortcutChanges() {
      this.taskShortcutDraftSelection = [...this.selectedTaskShortcutNames];
      this.taskShortcutChangesPending = false;
      this.showTaskShortcutManager = false;
    },
    saveTaskShortcutSelection() {
      this.selectedTaskShortcutNames = this.normalizeTaskSelection(
        this.taskShortcutDraftSelection
      );
      this.persistTaskShortcuts();
      this.showTaskShortcutManager = false;
      this.taskShortcutChangesPending = false;
    },
    openRunTaskModal(task) {
      this.$set(task, "requiredInputParams", []);
      this.$set(task, "optionalInputParams", []);
      this.selectedTaskDefinition = task;
      this.$http
        .get(
          this.$config.aimsAPI +
            "tasks/definitions/" +
            task.name +
            "/params/input"
        )
        .then(
          (response) => {
            this.selectedTaskDefinition.requiredInputParams =
              response.data.filter((r) => r.required == true);
            this.selectedTaskDefinition.optionalInputParams =
              response.data.filter((r) => r.required == false);
            this.$refs.runTaskModal.open();
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Task Parameters", error);
          }
        );
    },
    fileSelected(event, inputParam) {
      const fileSelected = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(fileSelected);
      reader.onload = function () {
        let encoded = reader.result.toString().replace(/^data:(.*,)?/, "");
        if (encoded.length % 4 > 0) {
          encoded += "=".repeat(4 - (encoded.length % 4));
        }
        inputParam.value = encoded;
        inputParam.meta = fileSelected.name;
      };
      reader.onerror = function (error) {
        console.error("Error selecting file: ", error);
        this.showError("Error selecting file");
      };
    },
    runTaskNow() {
      this.selectedTaskDefinition.requiredInputParams.forEach((rp) => {
        if (!rp.value || rp.value == "" || rp.value.length == 0) {
          this.$set(rp, "hasError", true);
        }
      });

      const paramValues = [];
      this.selectedTaskDefinition.requiredInputParams.forEach((rp) => {
        paramValues.push({
          name: rp.name,
          type: rp.type,
          value: rp.value,
          meta: rp.meta,
        });
      });

      this.selectedTaskDefinition.optionalInputParams.forEach((rp) => {
        paramValues.push({
          name: rp.name,
          type: rp.type,
          value: rp.value,
          meta: rp.meta,
        });
      });

      if (
        this.selectedTaskDefinition.requiredInputParams.filter(
          (rp) => rp.hasError == true
        ).length === 0
      ) {
        this.$refs.runTaskModal.isLoading();
        
        this.$http
          .post(
            this.$config.aimsAPI +
              "tasks/definitions/run?taskName=" +
              this.selectedTaskDefinition.name +
              "&runInForeground=" +
              this.$config.runInForeground,
            paramValues
          )
          .then(
            () => {
              this.$refs.runTaskModal.close();
              this.showSuccess("Task queued successfully");
            },
            (error) => {
              console.error(error);
              this.showError("Error Running Task", error);
            }
          );
      }
    },
  },
  watch: {
    shortcutDraftSelection: {
      deep: true,
      handler() {
        const normalizedDraft = this.normalizeSelection(
          this.shortcutDraftSelection
        );
        this.shortcutChangesPending =
          JSON.stringify(normalizedDraft) !==
          JSON.stringify(this.selectedShortcutRouteNames);
      },
    },
    taskShortcutDraftSelection: {
      deep: true,
      handler() {
        const normalizedDraft = this.normalizeTaskSelection(
          this.taskShortcutDraftSelection
        );
        this.taskShortcutChangesPending =
          JSON.stringify(normalizedDraft) !==
          JSON.stringify(this.selectedTaskShortcutNames);
      },
    },
    "$root.userGroups": {
      immediate: true,
      handler() {
        if (this.allShortcutOptions.length > 0) {
          this.refreshShortcutPermissions();
        }
        // Refresh task shortcuts when user groups change
        if (this.taskShortcutOptions.length > 0) {
          this.initializeTaskShortcuts();
        }
      },
    },
  },
};
</script>

<style scoped>
.shortcut-buttons h5 {
  font-weight: 600;
}
.shortcut-manager-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #ededed;
  border-radius: 4px;
  padding: 0.75rem;
}
.shortcut-category + .shortcut-category {
  border-top: 1px solid #f1f1f1;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}
.shortcut-category-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
}
.shortcut-manager .form-check + .form-check {
  border-top: 1px solid #f2f2f2;
}
.shortcut-manager label {
  cursor: pointer;
}
.btn-shortcut {
  background: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  padding: 1rem;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.btn-shortcut:hover {
  background-color: #f6f8fa;
  border-color: #c9d1d9;
}
.shortcut-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #007bff;
}
.shortcut-title {
  font-size: 1rem;
  font-weight: 600;
  word-break: break-word;
  white-space: normal;
}
/* Task Shortcut Styles */
.task-shortcut-manager .form-check + .form-check {
  border-top: 1px solid #f2f2f2;
}
.task-shortcut-manager label {
  cursor: pointer;
}
.task-shortcut-buttons .card-header h5 {
  font-weight: 600;
  color: #495057;
}
.btn-task-shortcut {
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border: 1px solid #c5cae9;
  border-radius: 6px;
  padding: 1rem;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.btn-task-shortcut:hover {
  background: linear-gradient(135deg, #f8f9ff 0%, #e8eaf6 100%);
  border-color: #7986cb;
  box-shadow: 0 2px 8px rgba(63, 81, 181, 0.15);
}
.task-shortcut-icon {
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #5c6bc0 0%, #3f51b5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #fff;
}
.task-shortcut-title {
  font-size: 0.95rem;
  font-weight: 600;
  word-break: break-word;
  white-space: normal;
  color: #3f51b5;
}
.task-shortcut-copy small {
  font-size: 0.75rem;
}
.dashboard-bottom-spacer {
  height: 300px;
}
</style>
